import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { cn } from '@/lib/utils'

export interface MentionUser {
  id: string
  firstName: string
  lastName: string
}

interface MentionListProps {
  items: MentionUser[]
  command: (item: { id: string; label: string }) => void
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    useEffect(() => {
      setSelectedIndex(0)
    }, [items])

    const selectItem = (index: number) => {
      const item = items[index]
      if (item) {
        command({ id: item.id, label: `${item.firstName} ${item.lastName}` })
      }
    }

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length)
          return true
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev + 1) % items.length)
          return true
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex)
          return true
        }
        return false
      },
    }))

    if (items.length === 0) {
      return (
        <div className="z-50 rounded-md border bg-popover p-2 text-popover-foreground shadow-md">
          <span className="text-xs text-muted-foreground">No users found</span>
        </div>
      )
    }

    return (
      <div className="z-50 rounded-md border bg-popover text-popover-foreground shadow-md overflow-hidden">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
              index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
            )}
            onClick={() => selectItem(index)}
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
              {item.firstName.charAt(0)}
              {item.lastName.charAt(0)}
            </div>
            <span>
              {item.firstName} {item.lastName}
            </span>
          </button>
        ))}
      </div>
    )
  },
)

MentionList.displayName = 'MentionList'
